import ExitToAppIcon from "@mui/icons-material/ExitToAppOutlined";
import PersonIcon from "@mui/icons-material/PersonOutlineOutlined";
import {
  Avatar,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import { lighten } from "@mui/material/styles";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAuth } from "./ParseAuthContext";
import { useTheme } from "../../context/ThemeContext";
import { getUserDisplayName } from "../../shared/userDisplayName";

/** How far the avatar is lightened from the primary colour of the app bar. */
const AVATAR_LIGHTEN = 0.75;

/**
 * The first letter of a user's name on a pale tint of the primary colour, in
 * that colour itself.
 */
export const InitialAvatar = ({
  name,
  size,
}: {
  name: string;
  /** The width and height, in px. */
  size: number;
}) => {
  const { theme } = useTheme();
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        bgcolor: lighten(theme.palette.primary.main, AVATAR_LIGHTEN),
        color: "primary.main",
        // the letter grows with the avatar
        fontSize: size * 0.4,
        fontWeight: 700,
      }}
    >
      {name.charAt(0).toUpperCase()}
    </Avatar>
  );
};

export const UserAvatar = () => {
  const { auth, logout } = useAuth();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { t } = useTranslation(undefined, {
    keyPrefix: "appBar",
  });
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [popoverAnchor, setPopoverAnchor] = useState<null | HTMLElement>(null);
  const isPopoverOpen = Boolean(popoverAnchor);
  const closePopover = () => {
    setPopoverAnchor(null);
  };
  if (auth.kind !== "signedIn") {
    navigate("/", { replace: true });
    return null;
  }

  const displayName = getUserDisplayName(auth.user) ?? t("anonymousUser");

  const handleAvatarClick = (event: MouseEvent<HTMLElement>) => {
    setPopoverAnchor(event.currentTarget);
  };
  const handleProfileClick = (_event: MouseEvent<HTMLElement>) => {
    navigate("/profile");
    closePopover();
  };
  const handleLogoutClick = async (_event: MouseEvent<HTMLElement>) => {
    // Note that these values come from state variables that we've declared before
    try {
      await logout();
      console.debug("Logged out, redirecting to login page");
      navigate("/", { replace: true });
    } catch (error) {
      // Error can be caused by wrong parameters or lack of Internet connection
      console.warn("Failed to log out", error);
    }
    closePopover();
  };

  return (
    <>
      <Tooltip title={displayName}>
        <IconButton onClick={handleAvatarClick} size="small" edge="end">
          <InitialAvatar name={displayName} size={36} />
        </IconButton>
      </Tooltip>

      <Popover
        open={isPopoverOpen}
        anchorEl={popoverAnchor}
        onClose={closePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <List disablePadding>
          <ListItemButton disableRipple onClick={handleProfileClick}>
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <PersonIcon />
            </ListItemIcon>
            {!isSmall && (
              <ListItemText
                sx={{ ml: theme.spacing(1) }}
                primary={t("userProfile.title")}
              />
            )}
          </ListItemButton>

          <Divider />

          <ListItemButton disableRipple onClick={handleLogoutClick}>
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <ExitToAppIcon />
            </ListItemIcon>
            {!isSmall && (
              <ListItemText
                sx={{ ml: theme.spacing(1) }}
                primary={t("logout")}
              />
            )}
          </ListItemButton>
        </List>
      </Popover>
    </>
  );
};
